/**
 * ConsultantAdvisoryBlock.tsx — Wave 129
 *
 * Visible E-E-A-T / consultant-positioning block for Blog articles and Store
 * (product) pages. Communicates that NYC Language Institute acts as an advisory
 * team (15+ years) rather than a document-running agency, and gives the reader
 * a concrete next step. No pricing, no guarantees, no JSON-LD (schema stays
 * owned by the page-level registry components).
 */
import { Link } from "@tanstack/react-router";
import { ShieldCheck, Users, ClipboardList, MessageCircle } from "lucide-react";

type Locale = "th" | "en";

export interface ConsultantAdvisoryBlockProps {
  locale?: Locale;
  /** Optional context noun, e.g. "Notary Public" / "Apostille". */
  topic?: string;
  variant?: "default" | "compact";
}

const COPY = {
  th: {
    eyebrow: "ที่ปรึกษา ไม่ใช่แค่ผู้รับจ้างเดินเอกสาร",
    title: (t?: string) =>
      t ? `ปรึกษาทีมงานก่อนเริ่มงาน ${t}` : "ปรึกษาทีมงานก่อนเริ่มดำเนินการ",
    lead:
      "ทีมงานของเรามีประสบการณ์มากกว่า 15 ปีในงานแปล รับรองเอกสาร และการยื่นต่อหน่วยงานราชการทั้งในและต่างประเทศ เราให้คำปรึกษาตั้งแต่การเลือกเส้นทางรับรองที่ถูกต้อง การจัดลำดับเอกสาร ไปจนถึงการแก้ไขเคสที่เคยถูกตีกลับ",
    points: [
      "ประเมินเคสก่อนเริ่มงาน เพื่อบอกได้ว่าเอกสารชุดใดจำเป็นจริง และชุดใดไม่ต้องทำ",
      "วางลำดับขั้นตอนให้ถูกต้องตั้งแต่ต้น ลดความเสี่ยงถูกตีกลับและต้องเริ่มใหม่",
      "อธิบายข้อกำหนดของปลายทาง (สถานทูต หน่วยงาน หรือหน่วยงานต่างประเทศ) ตามที่ประกาศไว้ ณ วันที่ให้คำปรึกษา",
      "หากคุณไม่อยากยุ่งยาก เราดูแลให้ครบทั้งกระบวนการ ตั้งแต่แปล รับรอง จนถึงรับเอกสารคืน",
    ],
    ctaAsk: "ปรึกษาเคสของคุณ",
    ctaTools: "เครื่องมือช่วยวางแผนเอกสาร",
    note: "ผลการพิจารณาขึ้นอยู่กับดุลพินิจของหน่วยงานปลายทาง เราให้คำแนะนำตามแนวปฏิบัติล่าสุดที่ตรวจสอบได้",
  },
  en: {
    eyebrow: "Advisors, not just a document-running agency",
    title: (t?: string) =>
      t ? `Talk to our team before you start ${t}` : "Talk to our advisory team before you start",
    lead:
      "Our team has over 15 years of experience in translation, certification, and filings with Thai and overseas authorities. We advise on choosing the correct certification route, sequencing your documents, and repairing files that were previously refused.",
    points: [
      "A case review before work begins, so you only prepare the documents that are genuinely required",
      "The correct order of steps from day one, reducing the risk of a refusal and a restart",
      "A plain explanation of the destination authority's stated requirements as published at the time of advice",
      "If you would rather not handle it yourself, we run the full process end to end and return the finished file",
    ],
    ctaAsk: "Review my case",
    ctaTools: "Document planning tools",
    note: "Final acceptance rests with the receiving authority. Our guidance follows the latest verifiable published practice.",
  },
} as const;

export default function ConsultantAdvisoryBlock({
  locale = "th",
  topic,
  variant = "default",
}: ConsultantAdvisoryBlockProps) {
  const L = COPY[locale];
  const askHref = locale === "th" ? "/ask" : "/en/ask";
  const toolsHref = "/tools/document-advisor";

  return (
    <section
      className="mx-auto my-10 max-w-5xl rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-amber-50/50 p-6 sm:p-8"
      aria-label={L.eyebrow}
      data-track-zone="consultant-advisory"
    >
      <div className="flex items-center gap-2 text-amber-700">
        <Users className="h-4 w-4" />
        <span className="text-[10px] font-bold uppercase tracking-widest">{L.eyebrow}</span>
      </div>

      <h2 className="mt-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        {L.title(topic)}
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">{L.lead}</p>

      {variant !== "compact" && (
        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
          {L.points.map((p) => (
            <li key={p} className="flex gap-2 text-sm text-slate-700">
              <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <Link
          to={askHref as never}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-navy-deep px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-md"
        >
          <MessageCircle className="h-4 w-4" /> {L.ctaAsk}
        </Link>
        <Link
          to={toolsHref as never}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-navy-deep transition hover:border-amber-500"
        >
          <ClipboardList className="h-4 w-4" /> {L.ctaTools}
        </Link>
      </div>

      <p className="mt-4 flex items-start gap-2 text-[11px] text-slate-500">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{L.note}</span>
      </p>
    </section>
  );
}
